/**
 * OrganizationDetailPage — Phase 3.
 *
 * Tabbed detail view for a single organization. Tabs:
 *   - Settings: rename, update description, change status.
 *   - Members: list, role change, remove (with confirm).
 *   - Invitations: list pending invites, send new, revoke.
 *   - Linked Artists: domain-link CRUD (the org-side of multi-tenancy).
 *
 * Write actions are gated by `myRole`. The backend is the source of truth;
 * UI gates are a courtesy, not a permission system.
 *
 * Built entirely out of existing primitives (Card, Tabs, Table, Dialog,
 * AlertDialog, Dropdown, Badge, Input, Textarea, Label, sonner toast).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Building2,
  Globe,
  Mail,
  Plus,
  Search,
  Trash2,
  Link as LinkIcon,
  Users,
} from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';

import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dropdown } from '../components/ui/dropdown';
import { Checkbox } from '../components/ui/checkbox';
import axiosInstance from '../utils/axiosInstance';
import { BASE_URL } from '../config/constants';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { ConfirmDialog } from '../components/ui/confirm-dialog';

import {
  createInvitation,
  createManagementWebsite,
  deactivateOrganization,
  deleteOrganization,
  getManagementWebsite,
  getOrganization,
  linkDomain,
  type ManagementWebsite,
  listDomainLinks,
  listInvitations,
  listMembers,
  reactivateOrganization,
  remindInvitation,
  removeMember,
  revokeInvitation,
  unlinkDomain,
  updateMember,
  updateOrganization,
  listLinkAssignments,
  assignLinkManager,
  unassignLinkManager,
  type Invitation,
  type Organization,
  type OrganizationDomainLink,
  type OrganizationMember,
  type OrganizationManagerAssignment,
  type ArtistDirectManager,
  type OrgRole,
} from '../services/organizationApi';
import { useOrganizations } from '../context/OrganizationContext';

const ROLE_OPTIONS: { value: OrgRole; label: string }[] = [
  { value: 'owner', label: 'Owner' },
  { value: 'admin', label: 'Company Admin' },
  { value: 'manager', label: 'Artist Representative' },
];

/** Display label for a management-company role. The DB role values
 *  (`owner`/`admin`/`manager`) are preserved on the wire; this is purely
 *  presentational. */
function roleDisplayLabel(role?: OrgRole | null): string {
  switch (role) {
    case 'owner': return 'Owner';
    case 'admin': return 'Company Admin';
    case 'manager': return 'Artist Representative';
    default: return '';
  }
}

function canManage(role?: OrgRole | null): boolean {
  return role === 'owner' || role === 'admin';
}

/**
 * Phase 3.5 — super-admin god-mode for org write access. The backend
 * already honors super-admin via requireOrgRole; the UI was hiding controls
 * because canManage(null) was false. Read user_type from the same storage
 * App.tsx writes to (no extra prop plumbing).
 */
function readUserTypeFromStorage(): string | null {
  try {
    const raw = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.user_type || null;
  } catch {
    return null;
  }
}

function readCurrentUserIdFromStorage(): string | null {
  try {
    const raw = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?._id || parsed?.id || null;
  } catch {
    return null;
  }
}

function readCurrentUserEmailFromStorage(): string | null {
  try {
    const raw = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.email === 'string' ? parsed.email.toLowerCase() : null;
  } catch {
    return null;
  }
}

// Populated `userId` can come back as `null` when the referenced User
// has been hard-deleted (super-admin user delete). Treat that as an
// orphan row instead of crashing the table render.
function memberUserId(m: OrganizationMember): string | null {
  const u = m.userId as OrganizationMember['userId'] | null;
  if (!u) return null;
  if (typeof u === 'string') return u;
  return u._id || null;
}

function memberDisplayName(m: OrganizationMember): string {
  const u = m.userId as OrganizationMember['userId'] | null;
  if (!u) return 'Deleted user';
  if (typeof u === 'string') return u;
  return u.fullName || u.username || u.email || u._id;
}

function memberSecondary(m: OrganizationMember): string | null {
  const u = m.userId as OrganizationMember['userId'] | null;
  if (!u) return 'Account no longer exists';
  if (typeof u === 'string') return null;
  return u.email || null;
}

const OrganizationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { organizations, loading: orgsLoading, refresh: refreshContext } = useOrganizations();

  const myRole = useMemo<OrgRole | null>(() => {
    const found = organizations.find((o) => o._id === id);
    return found?.myRole || null;
  }, [organizations, id]);
  const isSuperAdmin = useMemo(() => readUserTypeFromStorage() === 'super-admin', []);
  const canEdit = isSuperAdmin || canManage(myRole);

  // Scoped Members (= artist managers) and non-members have no business on
  // a management-company detail page. Kick them home once memberships load.
  useEffect(() => {
    if (orgsLoading) return;
    if (isSuperAdmin) return;
    if (!canManage(myRole)) navigate('/', { replace: true });
  }, [orgsLoading, isSuperAdmin, myRole, navigate]);

  const [activeTab, setActiveTab] = useState<string>('settings');
  const [inviteOpen, setInviteOpen] = useState<boolean>(false);
  const [org, setOrg] = useState<Organization | null>(null);
  const [loadingOrg, setLoadingOrg] = useState(true);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [artistDirectManagers, setArtistDirectManagers] = useState<ArtistDirectManager[]>([]);
  const [invites, setInvites] = useState<Invitation[]>([]);
  const [links, setLinks] = useState<OrganizationDomainLink[]>([]);
  // The company's own management website (null when it has none).
  const [website, setWebsite] = useState<ManagementWebsite | null>(null);
  const [creatingWebsite, setCreatingWebsite] = useState(false);

  const loadAll = useCallback(async () => {
    if (!id) return;
    setLoadingOrg(true);
    try {
      const [o, m, i, l, w] = await Promise.all([
        getOrganization(id),
        listMembers(id).catch(() => ({ members: [], artistDirectManagers: [] })),
        listInvitations(id).catch(() => []),
        listDomainLinks(id).catch(() => []),
        getManagementWebsite(id).catch(() => null),
      ]);
      setOrg(o);
      setMembers(m.members);
      setArtistDirectManagers(m.artistDirectManagers);
      setInvites(i);
      setLinks(l);
      setWebsite(w);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to load management company');
    } finally {
      setLoadingOrg(false);
    }
  }, [id]);

  const handleCreateWebsite = async () => {
    if (!id || !org) return;
    setCreatingWebsite(true);
    try {
      const created = await createManagementWebsite(id, { slug: org.slug });
      setWebsite(created);
      toast.success('Management website created');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to create the management website');
    } finally {
      setCreatingWebsite(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  if (loadingOrg && !org) {
    return (
      <div className="p-8 flex items-center justify-center text-muted-foreground">
        <Spinner size="md" className="mr-2" /> Loading management company…
      </div>
    );
  }

  if (!org) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-muted-foreground mb-4">
          Management company not found or you no longer have access.
        </p>
        <Button variant="outline" onClick={() => navigate('/organizations')}>
          Back to Management Companies
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8">
      <button
        onClick={() => navigate('/organizations')}
        className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> All management companies
      </button>

      <div className="flex items-start gap-3 mb-6">
        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <Building2 className="w-6 h-6 text-primary" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl lg:text-2xl font-bold truncate">{org.name}</h1>
            {org.status !== 'active' && (
              <Badge variant="destructive" className="capitalize">{org.status}</Badge>
            )}
            {myRole && (
              <Badge variant="secondary" className="capitalize">{roleDisplayLabel(myRole)}</Badge>
            )}
          </div>
          <div className="text-xs text-muted-foreground font-mono mt-0.5">/{org.slug}</div>
        </div>
      </div>

      {/* Management website status card */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Globe className="w-4 h-4 text-primary" /> Management Website
          </CardTitle>
        </CardHeader>
        <CardContent>
          {website ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-sm">{website.slug}</span>
                  <Badge variant={website.isPublished ? 'default' : 'secondary'}>
                    {website.isPublished ? 'Published' : 'Draft'}
                  </Badge>
                  {website.approvalStatus === 'approved' && (
                    <Badge variant="outline" className="text-green-600 border-green-200">Approved</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Select this website in the domain switcher to manage its sections
                  (Artist Roster, Testimonials, Our Clients, Team).
                </p>
              </div>
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => window.open(website.url, '_blank', 'noopener,noreferrer')}
              >
                <Globe className="w-4 h-4" /> Open Website
              </Button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <p className="text-sm text-muted-foreground flex-1">
                This company has no website yet. Creating one provisions a site with
                the management-only sections at{' '}
                <span className="font-mono">/{org.slug}</span>.
              </p>
              {isSuperAdmin ? (
                <Button className="gap-2" onClick={handleCreateWebsite} disabled={creatingWebsite}>
                  <Plus className="w-4 h-4" />
                  {creatingWebsite ? 'Creating…' : 'Create Website'}
                </Button>
              ) : (
                <Badge variant="secondary">Super admin creates websites</Badge>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="people">Team Members</TabsTrigger>
          <TabsTrigger value="domains">Linked Artists</TabsTrigger>
        </TabsList>

        <TabsContent value="settings">
          <SettingsTab
            org={org}
            canEdit={canEdit}
            canManageLifecycle={isSuperAdmin || myRole === 'owner'}
            onSaved={(next) => {
              setOrg(next);
              refreshContext();
            }}
            onDeleted={() => {
              refreshContext();
              navigate('/organizations');
            }}
          />
        </TabsContent>

        <TabsContent value="people" className="space-y-8">
          <MembersTab
            orgId={org._id}
            members={members}
            artistDirectManagers={artistDirectManagers}
            canEdit={canEdit}
            myRole={myRole}
            currentUserId={readCurrentUserIdFromStorage()}
            onChanged={loadAll}
            onInviteClick={() => setInviteOpen(true)}
          />
          <InvitationsTab
            orgId={org._id}
            invites={invites}
            links={links}
            canEdit={canEdit}
            myRole={myRole}
            currentUserId={readCurrentUserIdFromStorage()}
            currentUserEmail={readCurrentUserEmailFromStorage()}
            onChanged={loadAll}
            inviteOpen={inviteOpen}
            onInviteOpenChange={setInviteOpen}
          />
        </TabsContent>

        <TabsContent value="domains">
          <DomainsTab
            orgId={org._id}
            links={links}
            members={members}
            canAllocate={isSuperAdmin}
            canAssign={canEdit}
            onChanged={loadAll}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ─── Settings Tab ─────────────────────────────────────────────────────────

const SettingsTab: React.FC<{
  org: Organization;
  canEdit: boolean;
  canManageLifecycle: boolean;
  onSaved: (next: Organization) => void;
  onDeleted: () => void;
}> = ({ org, canEdit, canManageLifecycle, onSaved, onDeleted }) => {
  const [name, setName] = useState(org.name);
  const [description, setDescription] = useState(org.description || '');
  const [saving, setSaving] = useState(false);
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteConfirmSlug, setDeleteConfirmSlug] = useState('');

  useEffect(() => {
    setName(org.name);
    setDescription(org.description || '');
  }, [org]);

  const dirty =
    name.trim() !== org.name ||
    (description || '') !== (org.description || '');

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    try {
      const next = await updateOrganization(org._id, {
        name: name.trim(),
        description: description.trim(),
      });
      toast.success('Management company updated');
      onSaved(next);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const isInactive = org.status === 'inactive'
    || org.status === 'suspended'
    || org.status === 'archived';

  const handleDeactivate = async () => {
    setLifecycleBusy(true);
    try {
      const next = await deactivateOrganization(org._id);
      toast.success('Management company deactivated');
      onSaved(next);
      setConfirmDeactivate(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to deactivate');
    } finally {
      setLifecycleBusy(false);
    }
  };

  const handleReactivate = async () => {
    setLifecycleBusy(true);
    try {
      const next = await reactivateOrganization(org._id);
      toast.success('Management company reactivated');
      onSaved(next);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to reactivate');
    } finally {
      setLifecycleBusy(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmSlug !== org.slug) return;
    setLifecycleBusy(true);
    try {
      const { cascade } = await deleteOrganization(org._id);
      toast.success(
        `Management company deleted — ${cascade.membersRemoved} member(s), `
        + `${cascade.domainLinksRevoked} link(s), `
        + `${cascade.invitationsRevoked} invitation(s) cleared.`,
      );
      setConfirmDelete(false);
      onDeleted();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to delete');
    } finally {
      setLifecycleBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">General Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="set-name">Name</Label>
              <Input
                id="set-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!canEdit}
                required
                minLength={2}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="set-slug">Slug</Label>
              <Input
                id="set-slug"
                value={org.slug}
                disabled
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Slug cannot be changed after creation.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="set-desc">Description</Label>
              <Textarea
                id="set-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={!canEdit}
                rows={3}
              />
            </div>
            {canEdit ? (
              <div className="pt-2">
                <Button type="submit" loading={saving} disabled={!dirty}>
                  Save Changes
                </Button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                You need owner or admin role to edit these settings.
              </p>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Phase 3.75 — Lifecycle controls. Owner / super-admin only. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Lifecycle</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium">Status</span>
                <Badge
                  variant={isInactive ? 'destructive' : 'secondary'}
                  className="capitalize"
                >
                  {org.status}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground max-w-md">
                {isInactive
                  ? 'Inactive management companies grant no runtime access. '
                    + 'Members keep their accounts but cannot use this org’s domains '
                    + 'until it is reactivated.'
                  : 'Active. Members can use linked artist domains based on their role.'}
              </p>
            </div>
            {canManageLifecycle && (
              <div>
                {isInactive ? (
                  <Button
                    variant="outline"
                    loading={lifecycleBusy}
                    onClick={handleReactivate}
                  >
                    Reactivate
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    disabled={lifecycleBusy}
                    onClick={() => setConfirmDeactivate(true)}
                  >
                    Deactivate
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {canManageLifecycle && (
        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="text-base text-destructive">Danger Zone</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 max-w-md">
                <div className="text-sm font-medium mb-1">Delete this management company</div>
                <p className="text-xs text-muted-foreground">
                  Removes all members, revokes all linked artist access, and
                  cancels pending invitations. Artist domains themselves are
                  not deleted. This action is not reversible from the UI.
                </p>
              </div>
              <Button
                variant="destructive"
                disabled={lifecycleBusy}
                onClick={() => {
                  setDeleteConfirmSlug('');
                  setConfirmDelete(true);
                }}
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete Management Company
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Deactivate confirmation */}
      <ConfirmDialog
        open={confirmDeactivate}
        onOpenChange={setConfirmDeactivate}
        title="Deactivate management company?"
        description={(
          <>
            Members will immediately lose access to linked artist domains
            granted through <strong>{org.name}</strong>. You can reactivate
            at any time. No data is deleted.
          </>
        )}
        confirmLabel="Deactivate"
        loading={lifecycleBusy}
        onConfirm={handleDeactivate}
      />

      {/* Delete confirmation — requires typing the slug */}
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete management company?"
        description={(
          <>
            This will revoke all member access, cancel pending invitations,
            and unlink all artist domains. Artist sites themselves remain
            intact. This cannot be undone from the UI.
          </>
        )}
        body={(
          <div className="space-y-3">
            <p>
              Type the org slug{' '}
              <code className="px-1 py-0.5 rounded bg-muted font-mono">{org.slug}</code>{' '}
              to confirm.
            </p>
            <Input
              value={deleteConfirmSlug}
              onChange={(e) => setDeleteConfirmSlug(e.target.value)}
              placeholder={org.slug}
              autoFocus
            />
          </div>
        )}
        confirmLabel="Delete permanently"
        loading={lifecycleBusy}
        disabled={deleteConfirmSlug !== org.slug}
        onConfirm={handleDelete}
      />
    </div>
  );
};

// ─── Members Tab ──────────────────────────────────────────────────────────

const MembersTab: React.FC<{
  orgId: string;
  members: OrganizationMember[];
  artistDirectManagers: ArtistDirectManager[];
  canEdit: boolean;
  myRole?: OrgRole | null;
  currentUserId?: string | null;
  onChanged: () => void;
  onInviteClick?: () => void;
}> = ({ orgId, members, artistDirectManagers, canEdit, myRole, currentUserId, onChanged, onInviteClick }) => {
  const [removing, setRemoving] = useState<OrganizationMember | null>(null);
  const [pendingRoleChange, setPendingRoleChange] = useState<string | null>(null);

  const isScopedAdmin = myRole === 'admin';
  // Hide orphans (user account hard-deleted) and members whose membership
  // was already removed — "delete is delete".
  const baseMembers = members.filter(
    (m) => !!memberUserId(m) && m.status !== 'removed',
  );
  const visibleMembers = isScopedAdmin && currentUserId
    ? baseMembers.filter((m) => {
        const uid = memberUserId(m);
        return uid === currentUserId || m.invitedBy === currentUserId;
      })
    : baseMembers;

  // Dedupe artist-direct managers against existing org members — if the same
  // user already sits in OrganizationMember, the org row wins (it carries
  // role + actions). The artist-direct surface is read-only attribution.
  const memberUserIds = new Set(
    visibleMembers.map((m) => memberUserId(m)).filter(Boolean) as string[],
  );
  const visibleArtistDirect = artistDirectManagers.filter(
    (a) => a.userId && !memberUserIds.has(a.userId._id),
  );
  const totalRosterCount = visibleMembers.length + visibleArtistDirect.length;

  const handleRoleChange = async (m: OrganizationMember, role: OrgRole) => {
    if (!canEdit) return;
    if (role === m.role) return;
    const userId = memberUserId(m);
    if (!userId) return;
    setPendingRoleChange(userId);
    try {
      await updateMember(orgId, userId, { role });
      toast.success('Member role updated');
      onChanged();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update role');
    } finally {
      setPendingRoleChange(null);
    }
  };

  const handleRemove = async () => {
    if (!removing) return;
    const userId = memberUserId(removing);
    if (!userId) {
      toast.error('This member references a deleted account and cannot be removed here.');
      setRemoving(null);
      return;
    }
    try {
      await removeMember(orgId, userId);
      toast.success('Member removed');
      setRemoving(null);
      onChanged();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to remove member');
    }
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <h3 className="text-section-title">Members ({totalRosterCount})</h3>
        {canEdit && onInviteClick && (
          <Button onClick={onInviteClick} className="gap-1">
            <Plus className="w-4 h-4" /> Invite Member
          </Button>
        )}
      </div>

      {totalRosterCount === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <Users className="w-full h-full" />
          </div>
          <h3 className="empty-state-title">No members yet</h3>
          <p className="empty-state-description">
            Invite a team member to get started.
          </p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Artists Assigned</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="text-left">
            {visibleMembers.map((m) => {
              const userId = memberUserId(m);
              const isOrphan = !userId;
              const isBusy = !!userId && pendingRoleChange === userId;
              const isSelf = !!(userId && currentUserId && userId === currentUserId);
              const canAdminManageMember = !isScopedAdmin || (!isSelf && m.invitedBy === currentUserId);
              const canMutate = !isOrphan && canEdit && m.role !== 'owner' && canAdminManageMember;
              // Scoped admins can only invite Artist Representatives, so they
              // also can't promote/demote between admin and manager. When the
              // role dropdown would only contain a single option (the
              // current role), render it as a static Badge instead of an
              // interactive (but no-op) dropdown — keeps Members consistent
              // with the Invite Member dialog where the Role select is
              // disabled for the same reason.
              const roleEditOptions = isScopedAdmin
                ? ROLE_OPTIONS.filter((o) => o.value === 'manager')
                : ROLE_OPTIONS.filter((o) => o.value !== 'owner');
              const canEditRole = canMutate && roleEditOptions.length > 1;
              return (
                <TableRow key={m._id}>
                  <TableCell>
                    <div className="font-medium">{memberDisplayName(m)}</div>
                    {memberSecondary(m) && (
                      <div className="text-xs text-muted-foreground">{memberSecondary(m)}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    {canEditRole ? (
                      <Dropdown
                        options={roleEditOptions}
                        value={m.role}
                        onValueChange={(v) => handleRoleChange(m, v as OrgRole)}
                        disabled={isBusy}
                        className="w-32"
                      />
                    ) : (
                      <Badge variant="secondary" className="capitalize">{roleDisplayLabel(m.role)}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {m.role === 'owner' || m.role === 'admin' ? (
                      <span className="text-xs text-muted-foreground italic">All artists</span>
                    ) : m.artistAssignments && m.artistAssignments.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {m.artistAssignments.map((a) => (
                          <Badge
                            key={a.domainSlug}
                            variant="outline"
                            className="font-normal"
                            title={`/${a.domainSlug}`}
                          >
                            {a.domainName || a.domainSlug}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={m.status === 'active' ? 'outline' : 'destructive'}
                      className="capitalize"
                    >
                      {m.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {canMutate && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRemoving(m)}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {visibleArtistDirect.map((a) => {
              const u = a.userId!;
              const displayName = u.fullName || u.username || u.email || 'Manager';
              const secondary = (u.fullName || u.username) ? (u.email || null) : null;
              const artist = a.addedByArtist;
              const artistName = artist?.fullName || artist?.username || artist?.email || a.domainName || a.domainSlug;
              return (
                <TableRow key={`adm-${a._id}`}>
                  <TableCell>
                    <div className="font-medium">{displayName}</div>
                    {secondary && (
                      <div className="text-xs text-muted-foreground">{secondary}</div>
                    )}
                    <div className="text-xs text-muted-foreground italic mt-0.5">
                      Added by {artistName}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="capitalize">Artist Representative</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-normal" title={`/${a.domainSlug}`}>
                      {a.domainName || a.domainSlug}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">active</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="text-xs text-muted-foreground">Artist-managed</span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title="Remove member?"
        description={removing && (
          <>
            <strong>{memberDisplayName(removing)}</strong> will lose access to
            this management company and all linked artists. This cannot be undone
            without re-inviting them.
          </>
        )}
        confirmLabel="Remove"
        onConfirm={handleRemove}
      />
    </>
  );
};

// ─── Invitations Tab ──────────────────────────────────────────────────────

const InvitationsTab: React.FC<{
  orgId: string;
  invites: Invitation[];
  /** Phase C2.5 — active allocations available for invite-time assignment. */
  links: OrganizationDomainLink[];
  canEdit: boolean;
  myRole?: OrgRole | null;
  currentUserId?: string | null;
  currentUserEmail?: string | null;
  onChanged: () => void;
  /** When provided, the in-tab Invite button is hidden and this controls
   * the dialog instead (so the button can live in the Members header). */
  inviteOpen?: boolean;
  onInviteOpenChange?: (open: boolean) => void;
}> = ({
  orgId,
  invites,
  links,
  canEdit,
  myRole,
  currentUserId,
  currentUserEmail,
  onChanged,
  inviteOpen,
  onInviteOpenChange,
}) => {
  const externallyControlled = onInviteOpenChange !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externallyControlled ? !!inviteOpen : internalOpen;
  const setOpen = (v: boolean) => {
    if (externallyControlled) onInviteOpenChange?.(v);
    else setInternalOpen(v);
  };
  const isScopedAdmin = myRole === 'admin';
  const [email, setEmail] = useState('');
  // Owner is reserved for the org founder; viewer is future. So the invite
  // dropdown surfaces Admin and Manager. Scoped admins can only invite
  // managers (admins inviting other admins is reserved for owners/super).
  const [role, setRole] = useState<OrgRole>('manager');
  const inviteRoleOptions = isScopedAdmin
    ? ROLE_OPTIONS.filter((o) => o.value === 'manager')
    : ROLE_OPTIONS.filter((o) => o.value === 'admin' || o.value === 'manager');
  const [selectedSlugs, setSelectedSlugs] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [remindingId, setRemindingId] = useState<string | null>(null);
  // Surface the one-time share link immediately after invite creation.
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareEmail, setShareEmail] = useState<string>('');
  const visibleInvites = invites.filter((inv) => {
    if (inv.status !== 'pending') return false;
    if (currentUserEmail && inv.email.toLowerCase() === currentUserEmail) return false;
    if (isScopedAdmin && currentUserId) return inv.invitedBy === currentUserId;
    return true;
  });

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (currentUserEmail && email.trim().toLowerCase() === currentUserEmail) {
      toast.error('Use a different email. You already have access to this organization.');
      return;
    }
    setSubmitting(true);
    try {
      const cleaned = email.trim().toLowerCase();
      const result = await createInvitation(orgId, {
        email: cleaned,
        role,
        pendingAssignments: role === 'manager' ? Array.from(selectedSlugs) : [],
      });
      setOpen(false);
      if (result.granted) {
        if (result.alreadyHadAccess) {
          toast.success(`${cleaned} already had access`);
        } else {
          toast.success(`Access granted to ${cleaned}`);
        }
      } else if (result.token) {
        setShareEmail(cleaned);
        setShareUrl(`${window.location.origin}/invite/${result.token}`);
        toast.success('Invitation emailed');
      } else {
        toast.success('Invitation created');
      }
      setEmail('');
      setRole('manager');
      setSelectedSlugs(new Set());
      onChanged();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to create invite');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemind = async (inv: Invitation) => {
    setRemindingId(inv._id);
    try {
      const { token } = await remindInvitation(orgId, inv._id);
      setShareEmail(inv.email);
      setShareUrl(`${window.location.origin}/invite/${token}`);
      toast.success('Reminder email sent');
      onChanged();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to send reminder email');
    } finally {
      setRemindingId(null);
    }
  };

  const copyShareUrl = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(`Invite link copied for ${shareEmail}`);
    } catch {
      toast.error('Copy failed. Select the link below and copy it manually.');
    }
  };

  const handleRevoke = async (inv: Invitation) => {
    try {
      await revokeInvitation(orgId, inv._id);
      toast.success('Invitation revoked');
      onChanged();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to revoke invitation');
    }
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <h3 className="text-section-title">Pending Invitations ({visibleInvites.length})</h3>
        {canEdit && !externallyControlled && (
          <Button onClick={() => setOpen(true)} className="gap-1">
            <Plus className="w-4 h-4" /> Invite Member
          </Button>
        )}
      </div>

      {visibleInvites.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <Mail className="w-full h-full" />
          </div>
          <h3 className="empty-state-title">No pending invitations</h3>
          <p className="empty-state-description">
            Use Invite Member to send a secure invitation link.
          </p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="text-left">
            {visibleInvites.map((inv) => (
              <TableRow key={inv._id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    {inv.email}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="secondary" className="capitalize">{roleDisplayLabel(inv.role)}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  {canEdit && inv.status === 'pending' && (
                    <div className="inline-flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={remindingId === inv._id}
                        onClick={() => handleRemind(inv)}
                      >
                        {remindingId === inv._id ? 'Sending…' : 'Resend'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRevoke(inv)}
                      >
                        Revoke
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invite team member</DialogTitle>
            <DialogDescription>
              We will email a secure invitation link to the person you add.
              You can also copy the link yourself after sending. If they need
              another link later, use Resend from the invite list.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="inv-email">Email</Label>
              <Input
                id="inv-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="name@example.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inv-role">Role</Label>
              <Dropdown
                options={inviteRoleOptions}
                value={role}
                onValueChange={(v) => setRole(v as OrgRole)}
                disabled={inviteRoleOptions.length <= 1}
              />
              <p className="text-xs text-muted-foreground">
                {role === 'admin'
                  ? 'Admins have org-wide access to every linked artist.'
                  : 'Managers only see the artists you pick below.'}
              </p>
            </div>
            {role === 'manager' && (
            <div className="space-y-1.5">
              <Label>Artist access</Label>
              <p className="text-xs text-muted-foreground">
                Pick which linked artists this manager will be able to
                open right after they accept.
              </p>
              {links.filter((l) => l.status === 'active').length === 0 ? (
                <div className="text-xs text-muted-foreground rounded border border-dashed p-3">
                  No artists are linked to this organization yet. The
                  manager will be able to join but will not see any artists
                  until your super-admin links one.
                </div>
              ) : (
                <div className="max-h-48 overflow-auto rounded border divide-y divide-border">
                  {links
                    .filter((l) => l.status === 'active')
                    .map((l) => {
                      const checked = selectedSlugs.has(l.domainSlug);
                      return (
                        <label
                          key={l._id}
                          className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-muted/50"
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v) =>
                              setSelectedSlugs((prev) => {
                                const next = new Set(prev);
                                if (v) next.add(l.domainSlug);
                                else next.delete(l.domainSlug);
                                return next;
                              })
                            }
                          />
                          <span>
                            {l.domainName || l.domainSlug}
                            {l.domainName && (
                              <span className="ml-2 text-xs text-muted-foreground font-mono">
                                /{l.domainSlug}
                              </span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                </div>
              )}
            </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={submitting}>
                Send Invitation
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Phase 4 — share-link dialog. Token is shown ONCE; never stored. */}
      <Dialog open={!!shareUrl} onOpenChange={(o) => !o && setShareUrl(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Invitation sent</DialogTitle>
            <DialogDescription>
              A one-time invitation link was emailed to <strong>{shareEmail}</strong>.
              You can also copy it here in case you want to send it directly.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              readOnly
              value={shareUrl || ''}
              onFocus={(e) => e.currentTarget.select()}
              className="font-mono text-xs"
            />
            <p className="text-xs text-muted-foreground">
              Copy the link above and send it in the way you normally work
              with your team.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareUrl(null)}>
              Done
            </Button>
            <Button onClick={copyShareUrl}>Copy Invite Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const DomainsTab: React.FC<{
  orgId: string;
  links: OrganizationDomainLink[];
  members: OrganizationMember[];
  /**
   * Phase C1 — only super-admin can allocate or revoke a domain. Org
   * owners/admins see the same list read-only; the platform team controls
   * the linkage centrally.
   */
  canAllocate: boolean;
  /**
   * Phase C2 — org owners/admins (and super-admin) can scope which
   * org members may access each allocated domain. Managers/viewers see
   * the list read-only.
   */
  canAssign: boolean;
  onChanged: () => void;
}> = ({ orgId, links, members, canAllocate, canAssign, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [removing, setRemoving] = useState<OrganizationDomainLink | null>(null);
  const [assigning, setAssigning] = useState<OrganizationDomainLink | null>(null);

  // All domains in the system (super-admin endpoint). Fetched lazily when
  // the allocate dialog opens so the page load stays cheap.
  const [allDomains, setAllDomains] = useState<
    Array<{ _id: string; slug: string; name?: string }> | null
  >(null);
  const [domainsLoading, setDomainsLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setSearch('');
      return;
    }
    // Initialise selection with currently-allocated slugs so unchecking
    // means "revoke" and checking new ones means "allocate".
    setSelected(new Set(allocatedSlugs));
    if (allDomains !== null) return;
    setDomainsLoading(true);
    axiosInstance
      .get(`${BASE_URL}/api/domain`)
      .then((res) => setAllDomains(Array.isArray(res.data) ? res.data : []))
      .catch(() => setAllDomains([]))
      .finally(() => setDomainsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const allocatedSlugs = useMemo(
    () => new Set(links.filter((l) => l.status === 'active').map((l) => l.domainSlug)),
    [links],
  );

  // The Manage-managers dialog only matters for company members whose
  // access is per-artist (Member tier). Owners and admins always see every
  // allocated artist, so when none of the company's active members fall
  // into the scope-able tier the dialog is just noise.
  const hasScopableMembers = useMemo(
    () =>
      members.some(
        (m) => m.status === 'active' && m.role === 'manager',
      ),
    [members],
  );

  const filteredDomains = useMemo(() => {
    if (!allDomains) return [];
    const q = search.trim().toLowerCase();
    return allDomains
      .filter((d) => {
        if (!q) return true;
        return (
          d.slug.toLowerCase().includes(q) ||
          (d.name || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        // Unallocated first, then alphabetic by slug.
        const aAlloc = allocatedSlugs.has(a.slug) ? 1 : 0;
        const bAlloc = allocatedSlugs.has(b.slug) ? 1 : 0;
        if (aAlloc !== bAlloc) return aAlloc - bAlloc;
        return a.slug.localeCompare(b.slug);
      });
  }, [allDomains, search, allocatedSlugs]);

  const toggleSlug = (slug: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const handleAllocate = async () => {
    const toAdd = Array.from(selected).filter((s) => !allocatedSlugs.has(s));
    const toRemove = Array.from(allocatedSlugs).filter((s) => !selected.has(s));
    if (toAdd.length === 0 && toRemove.length === 0) {
      setOpen(false);
      return;
    }
    setSubmitting(true);
    const results = await Promise.allSettled([
      ...toAdd.map((s) => linkDomain(orgId, { domainSlug: s, accessLevel: 'manager' })),
      ...toRemove.map((s) => unlinkDomain(orgId, s)),
    ]);
    const failed = results.filter((r) => r.status === 'rejected').length;
    const succeeded = results.length - failed;
    if (succeeded > 0) {
      const parts: string[] = [];
      const addedOk = toAdd.length - results.slice(0, toAdd.length).filter((r) => r.status === 'rejected').length;
      const removedOk = toRemove.length - results.slice(toAdd.length).filter((r) => r.status === 'rejected').length;
      if (addedOk > 0) parts.push(`Linked ${addedOk}`);
      if (removedOk > 0) parts.push(`Revoked ${removedOk}`);
      toast.success(parts.join(' · '));
    }
    if (failed > 0) {
      toast.error(`${failed} change(s) failed`);
    }
    setSubmitting(false);
    setOpen(false);
    onChanged();
  };

  const handleUnlink = async () => {
    if (!removing) return;
    try {
      await unlinkDomain(orgId, removing.domainSlug);
      toast.success('Link revoked');
      setRemoving(null);
      onChanged();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to revoke link');
    }
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <h3 className="text-section-title">Linked Artists ({links.length})</h3>
        {canAllocate && (
          <Button onClick={() => setOpen(true)} className="gap-1">
            <LinkIcon className="w-4 h-4" /> Link Artist
          </Button>
        )}
      </div>

      {links.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <LinkIcon className="w-full h-full" />
          </div>
          <h3 className="empty-state-title">No linked artists yet</h3>
          <p className="empty-state-description">
            {canAllocate
              ? 'Link an artist domain to grant this organization\u2019s members access to its admin console.'
              : 'The Artist Intro team links artists centrally; reach out if you\u2019re expecting one here.'}
          </p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Artist</TableHead>
              <TableHead>Access Level</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="text-left">
            {links.map((l) => (
              <TableRow key={l._id}>
                <TableCell>
                  <div className="font-medium">{l.domainName || l.domainSlug}</div>
                  {l.domainName && (
                    <div className="text-xs text-muted-foreground font-mono">/{l.domainSlug}</div>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="secondary" className="capitalize">{l.accessLevel}</Badge>
                </TableCell>
                <TableCell>
                  <Badge
                    variant={l.status === 'active' ? 'outline' : 'destructive'}
                    className="capitalize"
                  >
                    {l.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center gap-1">
                    {canAssign && l.status === 'active' && hasScopableMembers && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setAssigning(l)}
                        title="Manage managers"
                      >
                        <Users className="w-4 h-4" />
                      </Button>
                    )}
                    {canAllocate && (
                      <Button variant="ghost" size="sm" onClick={() => setRemoving(l)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Link an artist</DialogTitle>
            <DialogDescription>
              Pick an artist domain to link to this organization.
              Organization members will gain access to the artist's admin
              console.
            </DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              autoFocus
              placeholder="Search by slug or name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="border border-border rounded-md max-h-80 overflow-y-auto">
            {domainsLoading ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground">
                <Spinner size="sm" className="mr-2" />
                Loading domains...
              </div>
            ) : filteredDomains.length === 0 ? (
              <div className="text-center py-10 text-sm text-muted-foreground">
                {search.trim()
                  ? 'No artists match your search.'
                  : 'No artist domains found.'}
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {filteredDomains.map((d) => {
                  const allocated = allocatedSlugs.has(d.slug);
                  const checked = selected.has(d.slug);
                  return (
                    <li key={d._id}>
                      <label className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/50">
                        <Checkbox
                          checked={checked}
                          disabled={submitting}
                          onCheckedChange={() => toggleSlug(d.slug)}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm truncate">
                            {d.name || d.slug}
                          </div>
                          {d.name && (
                            <div className="text-xs text-muted-foreground font-mono truncate">
                              /{d.slug}
                            </div>
                          )}
                        </div>
                        {allocated && (
                          <Badge variant="secondary" className="shrink-0">
                            Linked
                          </Badge>
                        )}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={submitting}
            >
              Close
            </Button>
            <Button
              type="button"
              onClick={handleAllocate}
              disabled={
                Array.from(selected).every((s) => allocatedSlugs.has(s)) &&
                Array.from(allocatedSlugs).every((s) => selected.has(s))
              }
              loading={submitting}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title="Revoke this link?"
        description={(
          <>
            Organization members will lose access to{' '}
            <strong>{removing?.domainName || removing?.domainSlug}</strong>’s admin console. The
            artist’s own data is untouched.
          </>
        )}
        confirmLabel="Revoke"
        onConfirm={handleUnlink}
      />

      <ManagerAssignmentDialog
        orgId={orgId}
        link={assigning}
        members={members}
        canMutate={canAssign}
        onClose={() => setAssigning(null)}
      />
    </>
  );
};

// ─── Manager Assignment Dialog (Phase C2) ───────────────────────────────────

const ManagerAssignmentDialog: React.FC<{
  orgId: string;
  link: OrganizationDomainLink | null;
  members: OrganizationMember[];
  canMutate: boolean;
  onClose: () => void;
}> = ({ orgId, link, members, canMutate, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<OrganizationManagerAssignment[]>([]);

  const open = !!link;
  const slug = link?.domainSlug || '';
  const displayName = link?.domainName || slug;

  const refresh = useCallback(async () => {
    if (!link) return;
    setLoading(true);
    try {
      const rows = await listLinkAssignments(orgId, link.domainSlug);
      setAssignments(rows);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  }, [orgId, link]);

  useEffect(() => {
    if (open) refresh();
    else setAssignments([]);
  }, [open, refresh]);

  const assignedUserIds = useMemo(() => {
    const s = new Set<string>();
    for (const a of assignments) {
      const u = a.userId as OrganizationManagerAssignment['userId'] | null;
      if (!u) continue;
      const uid = typeof u === 'string' ? u : u._id;
      if (uid) s.add(String(uid));
    }
    return s;
  }, [assignments]);

  // Only ACTIVE members with a still-existing User account. Orphan rows
  // (User hard-deleted) cannot be assigned — they will be removed by the
  // user-delete cascade on the backend.
  const activeMembers = useMemo(
    () => members.filter((m) => m.status === 'active' && memberUserId(m)),
    [members],
  );

  const handleToggle = async (m: OrganizationMember) => {
    if (!link) return;
    const uid = memberUserId(m);
    if (!uid) return;
    setBusyUserId(String(uid));
    try {
      if (assignedUserIds.has(String(uid))) {
        await unassignLinkManager(orgId, link.domainSlug, String(uid));
        toast.success('Assignment removed');
      } else {
        await assignLinkManager(orgId, link.domainSlug, String(uid));
        toast.success('Member assigned');
      }
      await refresh();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Assignment update failed');
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Manage managers — {displayName}
            {link?.domainName && (
              <span className="ml-2 text-xs text-muted-foreground font-mono">/{slug}</span>
            )}
          </DialogTitle>
          <DialogDescription>
            Owners and Company Admins always have access to every linked artist.
            Artist Representatives only see artists they've been assigned to.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Spinner size="sm" className="mr-2" /> Loading…
          </div>
        ) : activeMembers.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No active members in this organization yet.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="text-right">Access</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activeMembers.map((m) => {
                const uid = String(memberUserId(m));
                const isWide = m.role === 'owner' || m.role === 'admin';
                const isAssigned = assignedUserIds.has(uid);
                const busy = busyUserId === uid;
                return (
                  <TableRow key={m._id}>
                    <TableCell>
                      <div className="font-medium">{memberDisplayName(m)}</div>
                      {memberSecondary(m) && (
                        <div className="text-xs text-muted-foreground">{memberSecondary(m)}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="capitalize">{roleDisplayLabel(m.role)}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {isWide ? (
                        <Badge variant="outline">Org-wide</Badge>
                      ) : (
                        <Button
                          size="sm"
                          variant={isAssigned ? 'outline' : 'default'}
                          disabled={!canMutate || busy}
                          loading={busy}
                          onClick={() => handleToggle(m)}
                        >
                          {isAssigned ? 'Assigned' : 'Assign'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OrganizationDetailPage;
